import { computed } from 'vue';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import type { ClickoutUrl } from '@/helpers/clickout-helper';
import { MonetizationType } from '@/@types/graphql-types';
import { isJwtvTvodOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { openJwTvAvodModal } from '@/features/jwtv-avod/helpers/jwtv-avod-helper';
import { isJWTVOffer } from '@/features/jwtv/config';
import { type MaybeRefOrGetter, toValue } from '@vueuse/core';
import type { PlayerModalTitle, PlayerModalOffer } from '@/features/title-player/types';

type OfferWithPackage = {
	package: {
		shortName: string;
		technicalName: string;
	};
	monetizationType: MonetizationType;
	streamUrl?: string | null;
};

const MODAL_PROVIDER_SHORT_NAMES = ['jwt', 'otp', 'pxp', 'brp', 'fit'];

const playerModalByProvider: Record<string, { cmp: () => Promise<unknown> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
	brollieplayer: {
		cmp: () => import('@/ui-components/video-player-modals/BrolliePlayerModal.vue'),
	},
};

export function useTitlePlayerModal(shortName: MaybeRefOrGetter<string>) {
	const { openRentalModal } = useRentalModal();

	const isModalProvider = computed(() => MODAL_PROVIDER_SHORT_NAMES.includes(toValue(shortName)));

	function openThirdPartyPlayerModal({
		offer,
		title,
		clickoutUrl,
	}: {
		offer: OfferWithPackage;
		title?: PlayerModalTitle;
		clickoutUrl: ClickoutUrl;
	}): void {
		const modalData = playerModalByProvider[offer.package.technicalName];
		ModalHelper.openModal(
			modalData.cmp,
			{
				offer,
				title,
				additionalContexts: getClickoutUrlContextsArray(clickoutUrl),
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}

	async function openPlayerModal({
		offer,
		title,
		clickoutUrl,
		rentalOptions,
	}: {
		offer: PlayerModalOffer;
		title?: PlayerModalTitle;
		clickoutUrl: ClickoutUrl;
		rentalOptions?: {
			forceSafeOpen?: boolean;
			overrideRedirectUrl?: boolean;
		};
	}): Promise<void> {
		trackClickout(clickoutUrl);

		if (isJwtvTvodOffer(offer)) {
			openRentalModal({
				offer: offer,
				title: title!,
				clickoutUrl,
				forceSafeOpen: rentalOptions?.forceSafeOpen,
				overrideRedirectUrl: rentalOptions?.overrideRedirectUrl ?? true,
			});
			return;
		}

		if (isJWTVOffer(offer)) {
			await openJwTvAvodModal({
				modalProps: {
					offer: offer,
					title,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl),
				},
			});
		} else {
			openThirdPartyPlayerModal({ offer, title, clickoutUrl });
		}
	}

	return { isModalProvider, openPlayerModal };
}
